import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import type { InventoryBox } from "@/lib/inventoryBoxApi";
import {
  fetchInventoryBoxPutawayContext,
  saveInventoryBoxPutaway,
} from "@/lib/inventoryBoxApi";
import {
  WarehouseRecountShelfField,
  warehouseRecountShelfDraftToStorageKey,
  warehouseRecountShelfStorageKeyToDraft,
} from "@/pages/warehouse/WarehouseRecountShelfField";

const PUTAWAY_HOVER_HINT =
  "Record where this carton sits on the floor. Tap an existing shelf in the list or type a new shelf label. "
  + "This does not change Megaventory on-hand.";

function warehouseLabel(locationName: string | null | undefined, hubLocationId: number): string {
  if (locationName != null && locationName.trim() !== "") {
    return locationName.trim();
  }
  return `Warehouse ${String(hubLocationId)}`;
}

export function InventoryBoxPutawayCard(props: {
  box: InventoryBox;
  disabled?: boolean;
  /** Fields only. The parent modal owns Save and Cancel. */
  plain?: boolean;
  onSubmitApi?: (api: { submit: () => void; canSubmit: boolean; pending: boolean }) => void;
  onSaved?: () => void;
}): JSX.Element {
  const { box, disabled = false } = props;
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();

  const [hubLocationId, setHubLocationId] = useState(box.location_id ?? 1);
  const [shelfDraft, setShelfDraft] = useState(
    warehouseRecountShelfStorageKeyToDraft(box.sublocation),
  );

  useEffect(() => {
    if (box.location_id != null) {
      setHubLocationId(box.location_id);
    }
    setShelfDraft(warehouseRecountShelfStorageKeyToDraft(box.sublocation));
  }, [box.hex, box.location_id, box.sublocation]);

  const putawayShelvesQuery = useQuery({
    queryKey: ["inventory-box-putaway", box.hex, hubLocationId, "shelves"],
    queryFn: () => fetchInventoryBoxPutawayContext(box.hex, hubLocationId, "shelves"),
    enabled: box.hex.trim() !== "",
    staleTime: 60_000,
  });

  const shelfSublocations = putawayShelvesQuery.data?.shelf_sublocations ?? [];
  const locationName = putawayShelvesQuery.data?.location_name ?? null;
  const materialTableTitle = `Other material at ${warehouseLabel(locationName, hubLocationId)}`;

  const shelfStorageKey = warehouseRecountShelfDraftToStorageKey(shelfDraft);
  const putawayDirty =
    (box.location_id ?? 0) !== hubLocationId || box.sublocation !== shelfStorageKey;

  const saveMutation = useMutation({
    mutationFn: () =>
      saveInventoryBoxPutaway(box.hex, hubLocationId, shelfStorageKey),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory-box", box.hex] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-box-events", box.hex] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-box-putaway", box.hex] });
      if (result.box.location_id != null) {
        setHubLocationId(result.box.location_id);
      }
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(result.box.sublocation));
      showToast({ message: "Sublocation saved.", tone: "success" });
      props.onSaved?.();
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const busy = putawayShelvesQuery.isLoading || putawayShelvesQuery.isFetching || saveMutation.isPending;
  const canSubmit = putawayDirty && !disabled && !busy;
  const saveMutateRef = useRef(saveMutation.mutate);
  saveMutateRef.current = saveMutation.mutate;
  const onSubmitApiRef = useRef(props.onSubmitApi);
  onSubmitApiRef.current = props.onSubmitApi;

  useEffect(() => {
    onSubmitApiRef.current?.({
      submit: () => saveMutateRef.current(),
      canSubmit,
      pending: saveMutation.isPending,
    });
  }, [canSubmit, saveMutation.isPending]);

  const fields = (
    <WarehouseRecountShelfField
      id="inventory-box-putaway-shelf"
      shelfDraft={shelfDraft}
      shelfSublocations={shelfSublocations}
      disabled={disabled || busy}
      referenceTableFirst
      referenceTableTitle={materialTableTitle}
      onShelfDraftChange={setShelfDraft}
    />
  );

  if (props.plain === true) {
    return <div className="space-y-4">{fields}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint={PUTAWAY_HOVER_HINT}>Set sublocation</CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        {fields}
        <div className="flex flex-wrap gap-2">
          <HubSaveButton
            type="button"
            dirty={putawayDirty}
            disabled={disabled || busy}
            onClick={() => saveMutation.mutate()}
          >
            Save sublocation
          </HubSaveButton>
        </div>
      </CardBody>
    </Card>
  );
}
